import { useCallback } from "react";

import { Box, Checkbox, Flex, SimpleGrid } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";
import { IconCirclePlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, DECIMAL_PROPS, NumberInput } from "components";

import {
  BankCreditRequestForm,
  BankCreditRequestMortgageResponse,
  BankCreditResponse,
} from "types";
import { AppUtils } from "utils";

interface BankCreditRequestMortgageProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  readOnly?: boolean;
}

type MortgageFormItem = Omit<BankCreditRequestMortgageResponse, "id" | "creditRequestId">;

type MortgageFormData = Omit<BankCreditRequestMortgageResponse, "id"> & { id?: number };

const tempMortgageObject: Partial<MortgageFormItem> = {};

const propertyName = "mortgageExpenses";

export const BankCreditRequestMortgage = ({
  credit,
  form,
  readOnly = false,
}: BankCreditRequestMortgageProps) => {
  const { t } = useTranslation();

  const getInitialData = useCallback(() => {
    const formValues: Partial<BankCreditRequestMortgageResponse>[] | undefined =
      credit[propertyName];
    let array = [];
    if (!formValues || formValues.length === 0) {
      array.push(tempMortgageObject);
    } else {
      array = formValues.map((el) => ({ ...el }));
    }
    return array;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateMonthlyMortgage = useCallback(
    (
      mortgageAmount: number,
      interestRate: number,
      bank: "BANK_NOW" | "CEMBRA_MONEY_AG" | "CORNERCARD_KREDIT",
    ) => {
      if (mortgageAmount <= 0) return undefined;

      // always use 3% interest rate
      if (bank === "BANK_NOW" || bank === "CORNERCARD_KREDIT") {
        return (mortgageAmount * 3) / 100 / 12;
      }

      // Cembra uses the actual interest rate from input + 1%
      if (bank === "CEMBRA_MONEY_AG" && interestRate > 0) {
        return (mortgageAmount * (interestRate + 1)) / 100 / 12;
      }

      return undefined;
    },
    [],
  );

  const mortgageForm = useForm({
    mode: "uncontrolled",
    initialValues: getInitialData(),
    onValuesChange(values) {
      updateCredit(values);
    },
  });

  const updateCredit = useDebouncedCallback(async (values: any) => {
    const tempArray: MortgageFormData[] = [];
    let totalMonthlyMortgage = 0;

    Object.keys(values).forEach((key) => {
      const item: Partial<BankCreditRequestMortgageResponse> = { ...values[key] };

      const isNotEmpty = Object.keys(item).some(
        (field) =>
          field !== "id" &&
          field !== "creditRequestId" &&
          item[field as keyof BankCreditRequestMortgageResponse] != null,
      );

      if (isNotEmpty) {
        const mortgageAmount = item.mortgageAmount || 0;
        const interestRate = item.interestRate || 0;

        const monthlyBn = calculateMonthlyMortgage(mortgageAmount, interestRate, "BANK_NOW");
        const monthlyCb = calculateMonthlyMortgage(mortgageAmount, interestRate, "CEMBRA_MONEY_AG");
        const monthlyCc = calculateMonthlyMortgage(
          mortgageAmount,
          interestRate,
          "CORNERCARD_KREDIT",
        );

        const backendItem: MortgageFormData = {
          ...item,
          creditRequestId: credit.id,
          monthlyMortgageBn: monthlyBn,
          monthlyMortgageCb: monthlyCb,
          monthlyMortgageCc: monthlyCc,
        };
        tempArray.push(backendItem);

        totalMonthlyMortgage += (monthlyBn || 0) + (monthlyCb || 0) + (monthlyCc || 0);
      }
    });

    form.setFieldValue(propertyName, tempArray as any);
    form.setFieldValue(
      "mortgageAmount",
      totalMonthlyMortgage > 0 ? totalMonthlyMortgage : undefined,
    );
  }, 300);

  const getMonthlyMortgageForDisplay = (
    index: number,
    bank: "BANK_NOW" | "CEMBRA_MONEY_AG" | "CORNERCARD_KREDIT",
  ) => {
    const mortgage = mortgageForm.getValues()[index];
    if (!mortgage) return undefined;

    const mortgageAmount = mortgage.mortgageAmount || 0;
    const interestRate = mortgage.interestRate || 0;

    return calculateMonthlyMortgage(mortgageAmount, interestRate, bank);
  };

  const mortgageKeys = Object.keys(mortgageForm.getValues());

  return (
    <Box mt={"md"}>
      {mortgageKeys.map((key: any, index: number) => (
        <Box key={key} mb={"sm"}>
          <SimpleGrid cols={4} mb="md">
            <Box>
              <Checkbox
                key={mortgageForm.key(`${index}.mortgageAndInterestRate`)}
                {...mortgageForm.getInputProps(`${index}.mortgageAndInterestRate`, {
                  type: "checkbox",
                })}
                disabled={readOnly}
                label={t("credit.mortgageAmountAndRate.label")}
                mb={4}
              />
              <Flex gap="sm">
                <Box style={{ flex: "8" }}>
                  <NumberInput
                    placeholder={t("credit.mortgageAmount.placeholder")}
                    key={mortgageForm.key(`${index}.mortgageAmount`)}
                    {...mortgageForm.getInputProps(`${index}.mortgageAmount`)}
                    min={0}
                    disabled={readOnly}
                  />
                </Box>
                <Box style={{ flex: "4" }}>
                  <NumberInput
                    placeholder={t("credit.interestRate.placeholder")}
                    key={mortgageForm.key(`${index}.interestRate`)}
                    {...mortgageForm.getInputProps(`${index}.interestRate`)}
                    min={0}
                    max={100}
                    {...DECIMAL_PROPS}
                    suffix=" %"
                    disabled={readOnly}
                  />
                </Box>
              </Flex>
            </Box>

            <NumberInput
              label={t("credit.monthlyMortgageBn.label")}
              value={getMonthlyMortgageForDisplay(index, "BANK_NOW")}
              min={0}
              {...DECIMAL_PROPS}
              disabled
              readOnly
            />

            <NumberInput
              label={t("credit.monthlyMortgageCb.label")}
              value={getMonthlyMortgageForDisplay(index, "CEMBRA_MONEY_AG")}
              min={0}
              {...DECIMAL_PROPS}
              disabled
              readOnly
            />

            <NumberInput
              label={t("credit.monthlyMortgageCc.label")}
              value={getMonthlyMortgageForDisplay(index, "CORNERCARD_KREDIT")}
              min={0}
              {...DECIMAL_PROPS}
              disabled
              readOnly
            />
          </SimpleGrid>
        </Box>
      ))}

      {!readOnly && (
        <Button
          onClick={() => AppUtils.addNewObject(mortgageForm, tempMortgageObject)}
          variant="transparent"
          leftSection={<IconCirclePlus size={18} />}
        >
          {t("credit.addMortgage.label")}
        </Button>
      )}
    </Box>
  );
};
